const tools = require('form/tools')();
const globals = require('form/globals');
import { flattenDeep } from 'lodash-es';

export function readSessionFromUrl() {
  const utmKeys = ['utm_source', 'utm_medium', 'utm_term', 'utm_campaign', 'utm_content'];

  try {
    const searchParams = new URL(window.location.href).searchParams;

    return utmKeys.reduce((previous, current) => {
      const value = searchParams.get(current);

      if (value) {
        previous[current] = value;
      }

      return previous;
    }, {});
  } catch (error) {
    return {};
  }
}

export function readSessionFromStorage() {
  var session = readSession();
  if (session) return JSON.parse(session);
  else return {};

  function readSession() {
    if (typeof sessionStorage !== 'undefined') {
      return sessionStorage.getItem('utm-session');
    }
  }
}

/** Removes first h1 field if two appears consecutively. */
function removeMultipleHeaders(fields) {
  for (let i = 0; i < fields.length; i++) {
    if (i + 1 < fields.length) {
      if (fields[i][1].includes('h1_') && fields[i + 1][1].includes('h1_')) {
        fields.splice(i, 1);
      }
    }
  }

  return fields;
}

export function generateLegacyLead(state) {
  const config = state.globalState.globalConfig;
  if (!config.nospam) throw new Error('missing nospam');

  const nospam = config.nospam;
  const allFields = state.fields;
  const includedFields = allFields.filter((field) => includeField(state, field));
  const fieldsMap = tools.listToDict(includedFields);

  const specialFields = {
    sys_nature: getNature(state, config, true),
    aux_url: globals.parent_url || window.location.href,
    sys_sim: Number(tools.getUrlParameters()['sim']) === 1 ? 1 : 0,
  };

  return Object.keys(nospam).reduce((dict, key) => {
    var value = getStateValue(key, fieldsMap);
    var name = nospam[key].name;

    if (specialFields[key] !== undefined) {
      dict[name] = specialFields[key];
    } else if (nospam[key].value) {
      dict[name] = nospam[key].value;
    } else dict[name] = value;

    return dict;
  }, {});
}

function getFieldGroup(config) {
  try {
    if (config && config.field_group) {
      return config.field_group;
    } else {
      return 'form';
    }
  } catch (e) {
    return 'form';
  }
}

export function generateLead(state) {
  const config = state.globalState.globalConfig;
  if (!config.nospam) throw new Error('missing nospam');

  const nospam = config.nospam;
  const allFields = state.fields;
  const includedFields = allFields.filter((field) => includeField(state, field));
  const fieldsMap = tools.listToDict(includedFields);
  const fieldOrdering = includedFields.reduce((ordering, { id }, index) => {
    ordering[id] = index;
    return ordering;
  }, {});

  const specialFields = {
    sys_nature: getNature(state, config, false),
    aux_url: globals.parent_url || window.location.href,
    sys_sim: Number(tools.getUrlParameters()['sim']) === 1 ? 1 : 0,
  };

  const removeFromFields = ['spinner', 'fields'];

  const fields = Object.keys(nospam)
    .filter((key) => {
      return removeFromFields.indexOf(key) === -1;
    })
    .sort((left, right) => {
      const leftOrdering = fieldOrdering[left];
      const rightOrdering = fieldOrdering[right];
      if (leftOrdering === rightOrdering) return 0;
      else if (leftOrdering === undefined) return -1;
      else if (rightOrdering === undefined) return 1;
      return leftOrdering - rightOrdering;
    })
    .map((key) => {
      var stateValue = getStateValue(key, fieldsMap);
      var name = nospam[key].name;
      var config = (fieldsMap[key] || {}).config || {};

      const getFieldProperties = () => {
        if (specialFields[key] !== undefined)
          return {
            value: specialFields[key],
            field_group: 'system' /* These are most likely sys_fields */,
          };
        else if (nospam[key].value)
          return {
            value: nospam[key].value,
            field_group: getFieldGroup(config),
          };
        else if (config.emptyValue !== undefined && value === '')
          return {
            value: String(config.emptyValue),
            field_group: getFieldGroup(config),
          };
        else
          return {
            //Adding option for hidden label to be used in lead data. hiddenLabel should only be used for fields that does not normally include a label
            field_display: config.label ?? config.hiddenLabel,
            value: stateValue,
            value_display:
              getStateDisplayValue(key, fieldsMap) ?? getDisplayValue(config, stateValue),
            field_group: getFieldGroup(config),
          };
      };
      const field_id = key === 'time' ? 't' : key;
      const { field_display, value, value_display, field_group } = getFieldProperties();
      if (field_id !== 'honeypot' && String(value).length === 0) return null;
      else return [name, field_id, field_display, value, value_display, field_group];
    })
    .filter((a) => a);

  return {
    s: nospam.spinner.value,
    fields: removeMultipleHeaders(fields),
  };
}

function getNature(state, config, isLegacy) {
  var primaryNature = tools.findStateProperties(state, 'nature')[0] || config.nature;
  var secondaryNatures = tools.findStateProperties(state, 'secondaryNature');
  if (isLegacy) return primaryNature;
  else return tools.unique([primaryNature, ...secondaryNatures]).join(',');
}

function getStateValue(key, state) {
  var field = state[key];
  if (field) {
    return field.state.value;
  } else {
    return '';
  }
}

// Components that build a composed value (e.g. items-counter) set value_display on
// the field state, since getDisplayValue cannot match a composed string against the
// option list.
function getStateDisplayValue(key, state) {
  var field = state[key];
  return field ? field.state.value_display : undefined;
}

/** Return display value for dropdowns and checkboxes */
function getDisplayValue(config, fieldValue) {
  const { options } = config;

  if (!options || options.length === 0) return null;

  return flattenDeep([fieldValue])
    .map((value) => {
      const foundOption = options.find((option) => option.value === value);
      return foundOption && foundOption.hasOwnProperty('display') ? foundOption.display : value;
    })
    .join(', ');
}

function includeField(state, field) {
  return tools.isFieldEnabled(state, field) && field.config.ignored !== true;
}
